import { ROTULO_DO_CAMPO_DE_PACIENTE as R } from "@bookdental/schemas";
import { centavosParaCelula, dataParaCelula, diaParaCelula, type ColunaExport } from "./exportar-tabela";

/**
 * Cabeçalhos das exportações.
 *
 * Não são rótulos livres: onde já existe importação, o título aqui é o mesmo que o auto-mapeamento
 * reconhece (`PATIENT_DB_FIELDS` e companhia em `@bookdental/schemas`). Por isso "Celular" e não
 * "Telefone", "CPF" e não "Documento" — o arquivo que sai tem que voltar sem ninguém remapear
 * coluna por coluna. Acento pode: o mapeamento normaliza antes de comparar.
 */

type Paciente = {
  id: string;
  name: string;
  identifier: string | null;
  document: string | null;
  sex: string | null;
  birthDate: Date | string | null;
  phone: string | null;
  email: string | null;
  tags: string[];
  active: boolean;
  city: string | null;
  state: string | null;
  createdAt?: Date | string;
};

// Cada coluna diz o campo que lê: o servidor só devolve os campos das colunas marcadas.
export const COLUNAS_PACIENTE: ColunaExport<Paciente>[] = [
  { campo: "name", titulo: R.name, valor: (p) => p.name },
  { campo: "document", titulo: R.document, valor: (p) => p.document },
  { campo: "identifier", titulo: R.identifier, valor: (p) => p.identifier },
  { campo: "birthDate", titulo: R.birthDate, valor: (p) => diaParaCelula(p.birthDate) },
  { campo: "sex", titulo: R.sex, valor: (p) => p.sex },
  { campo: "phone", titulo: R.phone, valor: (p) => p.phone },
  { campo: "email", titulo: R.email, valor: (p) => p.email },
  { campo: "city", titulo: R.city, valor: (p) => p.city },
  { campo: "state", titulo: R.state, valor: (p) => p.state },
  { campo: "tags", titulo: R.tags, valor: (p) => (p.tags ?? []).join(", ") },
  { campo: "active", titulo: R.active, valor: (p) => (p.active ? "Sim" : "Não") },
  { campo: "createdAt", titulo: R.createdAt, valor: (p) => dataParaCelula(p.createdAt) },
];

type Parceiro = {
  id: string;
  name: string | null;
  type: string;
  status: string;
  document: string | null;
  phone: string | null;
  email: string | null;
  city?: string | null;
  state?: string | null;
  isPartner?: boolean;
  createdAt?: Date | string;
};

export function colunasParceiro(rotuloDoTipo: (t: string) => string): ColunaExport<Parceiro>[] {
  return [
    { titulo: "Nome", valor: (p) => p.name },
    { titulo: "Tipo", valor: (p) => rotuloDoTipo(p.type) },
    { titulo: "CPF/CNPJ", valor: (p) => p.document },
    { titulo: "Celular", valor: (p) => p.phone },
    { titulo: "E-mail", valor: (p) => p.email },
    { titulo: "Cidade", valor: (p) => p.city ?? "" },
    { titulo: "UF", valor: (p) => p.state ?? "" },
    { titulo: "Situação", valor: (p) => (p.status === "ACTIVE" ? "Ativo" : "Inativo") },
    // Conectado é diferente de cadastrado: o parceiro existe na sua lista mesmo sem conta na
    // plataforma, e quem lê a planilha precisa saber com quem dá pra trocar pedido de verdade.
    { titulo: "Conectado", valor: (p) => (p.isPartner ? "Sim" : "Não") },
    { titulo: "Cadastrado em", valor: (p) => dataParaCelula(p.createdAt) },
  ];
}

type Membro = {
  id: string;
  role: string;
  status: string;
  user: { name: string | null; email: string | null; croNumber?: string | null; croState?: string | null };
  customRole?: { label: string } | null;
  especialidade?: { rotulos: string[]; origem: string };
  assignedProcedureTypeIds?: string[];
};

export function colunasMembro(rotuloDoPapel: (role: string) => string): ColunaExport<Membro>[] {
  return [
    { titulo: "Nome", valor: (m) => m.user.name },
    { titulo: "E-mail", valor: (m) => m.user.email },
    { titulo: "Papel", valor: (m) => m.customRole?.label ?? rotuloDoPapel(m.role) },
    { titulo: "Especialidade", valor: (m) => (m.especialidade?.rotulos ?? []).join(", ") },
    {
      titulo: "Origem da especialidade",
      valor: (m) =>
        m.especialidade?.origem === "organizacao"
          ? "Esta organização"
          : m.especialidade?.origem === "perfil"
            ? "Perfil pessoal"
            : "",
    },
    { titulo: "CRO", valor: (m) => [m.user.croNumber, m.user.croState].filter(Boolean).join("-") },
    { titulo: "Situação", valor: (m) => (m.status === "ACTIVE" ? "Ativo" : "Suspenso") },
    { titulo: "Responsabilidades", valor: (m) => (m.assignedProcedureTypeIds ?? []).length },
  ];
}

type Servico = {
  id: string;
  status: string;
  priceCents: number | null;
  quantidade?: number | null;
  teeth: string | null;
  notes: string | null;
  concludedAt?: Date | string | null;
  serviceCategory?: { label: string } | null;
  materialCategory?: { label: string } | null;
  materialLine?: { name: string } | null;
  shadeOption?: { code: string } | null;
  productionStage?: { label: string } | null;
  modelStage?: { label: string } | null;
  assignedToUser?: { name: string | null } | null;
};

type LinhaDePedido = {
  pedido: {
    id: string;
    patientName: string;
    dentistName: string | null;
    organization: { name: string; isPersonal: boolean };
    createdAt: Date | string;
    deadline: Date | string | null;
    status: string;
  };
  servico: Servico;
};

/**
 * Pedidos e produção saem por SERVIÇO, uma linha cada.
 *
 * Uma linha por pedido obrigaria a espremer "3 serviços, 2 materiais, 2 cores" numa célula — some a
 * soma por material, some o filtro por cor, e o extrato deixa de servir. Os dados do pedido se
 * repetem nas linhas irmãs, que é o formato que qualquer tabela dinâmica espera.
 */
type LinhaDaPlanilha = { order: LinhaDePedido["pedido"] & { deadline?: any }; service: Servico };

/**
 * O valor de cada coluna da planilha de produção, indexado pela mesma chave da tela.
 *
 * Existe pra que exportar respeite as colunas que a pessoa configurou: ordem e o que ela escondeu.
 * "Seleção", "Finalizar", "Abrir pedido" e "Anexo" não têm valor exportável — são ações — e por isso
 * não aparecem aqui; a exportação simplesmente pula o que não sabe traduzir.
 */
export function valoresDaPlanilha(
  rotuloDoStatus: (s: string) => string,
): Partial<Record<string, (l: LinhaDaPlanilha) => string | number | null | undefined>> {
  return {
    status: (l) => rotuloDoStatus(l.service.status),
    modelo: (l) => l.service.modelStage?.label,
    estagio: (l) => l.service.productionStage?.label,
    data: (l) => dataParaCelula(l.order.createdAt),
    prazo: (l) => dataParaCelula(l.order.deadline),
    servico: (l) => l.service.serviceCategory?.label,
    responsavel: (l) => l.service.assignedToUser?.name,
    clinica: (l) => (l.order.organization.isPersonal ? l.order.dentistName : l.order.organization.name),
    dentista: (l) => l.order.dentistName,
    paciente: (l) => l.order.patientName,
    dentes: (l) => l.service.teeth,
    material: (l) => l.service.materialCategory?.label ?? l.service.materialLine?.name,
    cor: (l) => l.service.shadeOption?.code,
    obs: (l) => l.service.notes,
  };
}

export function colunasServico(rotuloDoStatus: (s: string) => string): ColunaExport<LinhaDePedido>[] {
  return [
    { titulo: "Paciente", valor: (l) => l.pedido.patientName },
    { titulo: "Clínica", valor: (l) => (l.pedido.organization.isPersonal ? l.pedido.dentistName : l.pedido.organization.name) },
    { titulo: "Dentista", valor: (l) => l.pedido.dentistName },
    { titulo: "Serviço", valor: (l) => l.servico.serviceCategory?.label },
    { titulo: "Dentes", valor: (l) => l.servico.teeth },
    { titulo: "Material", valor: (l) => l.servico.materialCategory?.label ?? l.servico.materialLine?.name },
    { titulo: "Cor", valor: (l) => l.servico.shadeOption?.code },
    { titulo: "Status", valor: (l) => rotuloDoStatus(l.servico.status) },
    { titulo: "Estágio", valor: (l) => l.servico.productionStage?.label },
    { titulo: "Etapa do modelo", valor: (l) => l.servico.modelStage?.label },
    { titulo: "Responsável", valor: (l) => l.servico.assignedToUser?.name },
    // O preço guardado é o unitário; o do serviço é preço × quantidade (arcada = 1, dente a dente = nº de dentes).
    { titulo: "Quantidade", valor: (l) => l.servico.quantidade ?? 1 },
    { titulo: "Valor unitário", valor: (l) => centavosParaCelula(l.servico.priceCents) },
    { titulo: "Valor", valor: (l) => centavosParaCelula(l.servico.priceCents == null ? null : l.servico.priceCents * (l.servico.quantidade ?? 1)) },
    { titulo: "Data do pedido", valor: (l) => dataParaCelula(l.pedido.createdAt) },
    { titulo: "Prazo", valor: (l) => dataParaCelula(l.pedido.deadline) },
    { titulo: "Concluído em", valor: (l) => dataParaCelula(l.servico.concludedAt) },
    { titulo: "Observações", valor: (l) => l.servico.notes },
    { titulo: "ID do pedido", valor: (l) => l.pedido.id },
  ];
}
